import React, { useEffect, useRef, useState } from 'react'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'

// Semicircular meter geometry. The arc is a 180° sweep; ARC_LENGTH is its
// path length, used for the stroke-dash sweep animation.
const RADIUS = 48
const STROKE = 10
const ARC_LENGTH = Math.PI * RADIUS
const ANIMATION_MS = 900

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

// Semi-donut gauge for a CQC key question: score sweeps in on mount with the
// number counting up in sync. `score` null/undefined renders the "No data"
// state (neutral track, no fill, no misleading 100).
const KeyQuestionGauge = ({ label, score, bandColor, onClick }) => {
  const theme = useTheme()
  const hasData = score !== null && score !== undefined
  const [progress, setProgress] = useState(0)
  const frameRef = useRef()

  useEffect(() => {
    if (!hasData) return
    if (prefersReducedMotion()) {
      setProgress(1)
      return
    }
    let start
    const tick = (now) => {
      if (start === undefined) start = now
      const t = Math.min((now - start) / ANIMATION_MS, 1)
      setProgress(easeOutCubic(t))
      if (t < 1) frameRef.current = requestAnimationFrame(tick)
    }
    frameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameRef.current)
  }, [hasData, score])

  const fillColor = theme.palette[bandColor]?.main ?? bandColor
  const trackColor = theme.palette.divider
  const shownScore = hasData ? Math.round(score * progress) : null
  const dashOffset = hasData
    ? ARC_LENGTH * (1 - (score / 100) * progress)
    : ARC_LENGTH

  return (
    <Tooltip
      title={
        hasData
          ? `${label}: ${score} / 100 — click to view gaps`
          : `${label}: no evidence recorded yet`
      }
    >
      <Paper
        variant="outlined"
        onClick={onClick}
        role="img"
        aria-label={
          hasData ? `${label} readiness ${score} out of 100` : `${label}: no data`
        }
        sx={{
          p: 2,
          width: '100%',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'box-shadow 0.15s ease',
          '&:hover': onClick && { boxShadow: 3 },
        }}
      >
        <Stack alignItems="center" spacing={0.5}>
          <svg
            viewBox="0 0 120 68"
            width="100%"
            style={{ maxWidth: 150, display: 'block' }}
          >
            <path
              d="M 12 62 A 48 48 0 0 1 108 62"
              fill="none"
              stroke={trackColor}
              strokeWidth={STROKE}
              strokeLinecap="round"
            />
            {hasData && (
              <path
                d="M 12 62 A 48 48 0 0 1 108 62"
                fill="none"
                stroke={fillColor}
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeDasharray={ARC_LENGTH}
                strokeDashoffset={dashOffset}
              />
            )}
            {hasData ? (
              <text
                x="60"
                y="56"
                textAnchor="middle"
                fontSize="26"
                fontWeight="700"
                fontFamily={theme.typography.fontFamily}
                fill={theme.palette.text.primary}
              >
                {shownScore}
              </text>
            ) : (
              <text
                x="60"
                y="54"
                textAnchor="middle"
                fontSize="12"
                fontWeight="600"
                fontFamily={theme.typography.fontFamily}
                fill={theme.palette.text.secondary}
              >
                No data
              </text>
            )}
          </svg>
          <Typography variant="body2" fontWeight={700}>
            {label}
          </Typography>
        </Stack>
      </Paper>
    </Tooltip>
  )
}

export default KeyQuestionGauge
